import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AVAILABLE_PERMISSIONS } from "../Config/permissions";
import { resetSubAdminPassword } from "../Redux/Action/SubAdmin";
import ConfirmationModal from "./ConfirmationModal";
import { toast } from "react-toastify";

const SubAdminForm = ({ initialValues, onSubmit, isEdit = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  
  // Log available permissions for debugging deployment issues
  console.log("DEBUG: AVAILABLE_PERMISSIONS count:", AVAILABLE_PERMISSIONS.length);
  console.log("DEBUG: WhatsApp Settings in list:", AVAILABLE_PERMISSIONS.some(p => p.id === "whatsapp-settings"));

  const [resetting, setResetting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const currentUser = useSelector((state) => state.SubAdmin.admindetails);
  const isSelf = id === currentUser?._id;

  const formik = useFormik({
    initialValues: initialValues || {
      name: "",
      email: "",
      permission: [],
      role: "Sub-Admin",
      isActive: true,
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      email: Yup.string().email().required("Email is required"),
      role: Yup.mixed()
        .oneOf(["Admin", "Sub-Admin"], "Invalid role selection")
        .required("Role is required"),
      name: Yup.string().required("User Name is required"),
      permission: Yup.array().min(1, "At least one permission is required").required("Permission is required"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await onSubmit(values);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleResetPassword = async () => {
    setShowResetModal(false);
    setResetting(true);
    try {
      await dispatch(resetSubAdminPassword(id)).unwrap();
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h4 style={{ margin: 0, fontWeight: '700', color: '#333' }}>
            {isEdit ? "Update Sub-Admin Profile" : "Create New Sub-Admin"}
          </h4>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              type="button" 
              className="common-btn" 
              style={{ backgroundColor: '#6c757d' }}
              onClick={() => navigate('/sub-admin-management')}
            >
              Back
            </button>
            {isEdit && (
              <button 
                type="button" 
                className="common-btn" 
                style={{ backgroundColor: '#dc3545' }}
                disabled={resetting}
                onClick={() => setShowResetModal(true)}
              >
                {resetting ? "Resetting..." : "Reset Password"}
              </button>
            )}
            <button 
              type="submit" 
              className="common-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (isEdit ? "Updating..." : "Saving...") : (isEdit ? "Update Changes" : "Save Sub-Admin")}
            </button>
          </div>
        </div>

        {!isEdit && (
          <div style={{ 
            backgroundColor: '#e7f3ff', 
            padding: '12px 20px', 
            borderRadius: '8px', 
            marginBottom: '20px', 
            borderLeft: '4px solid #007bff',
            display: 'flex',
            alignItems: 'center'
          }}>
            <i className="fas fa-info-circle" style={{ color: '#007bff', marginRight: '10px', fontSize: '18px' }}></i>
            <span style={{ color: '#0056b3', fontWeight: '500' }}>
              Note: A secure random password will be automatically generated and sent to the sub-admin's email address upon saving.
            </span>
          </div>
        )}

        {isEdit && (
          <div style={{ 
            backgroundColor: '#fff3cd', 
            padding: '12px 20px', 
            borderRadius: '8px', 
            marginBottom: '20px', 
            borderLeft: '4px solid #ffc107',
            display: 'flex',
            alignItems: 'center'
          }}>
            <i className="fas fa-exclamation-triangle" style={{ color: '#856404', marginRight: '10px', fontSize: '18px' }}></i>
            <span style={{ color: '#856404', fontWeight: '500' }}>
              Note: Use the "Reset Password" button above if you need to generate and send a new password to this sub-admin.
            </span>
          </div>
        )}

        <div className="user_list_wrap">
          <div className="row">
            <div className="col-md-12">
              <div className="formwrap-bcx" style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                <div className="row">
                  <div className="col-md-5">
                    <div className="form-group">
                      <label style={{ fontWeight: '600', marginBottom: '8px' }}>User Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="User Name"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                        name="name"
                        style={{ borderRadius: '8px' }}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <div className="error" style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>{formik.errors.name}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div className="form-group">
                      <label style={{ fontWeight: '600', marginBottom: '8px' }}>Email</label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Email Address"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        name="email"
                        style={{ borderRadius: '8px' }}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="error" style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>{formik.errors.email}</div>
                      )}
                    </div>
                  </div>
                  <div className={isEdit ? "col-md-2" : "col-md-2"}>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', marginBottom: '8px' }}>Role Name</label>
                      <select
                        className="form-control"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.role}
                        name="role"
                        disabled={isSelf || (currentUser?.role === 'Sub-Admin')}
                        style={{ borderRadius: '8px', cursor: (isSelf || currentUser?.role === 'Sub-Admin') ? 'not-allowed' : 'default' }}
                      >
                        <option value="Sub-Admin">Sub-Admin</option>
                        <option value="Admin">Admin</option>
                      </select>
                      {(isSelf || currentUser?.role === 'Sub-Admin') && (
                        <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Role cannot be changed</div>
                      )}
                      {formik.touched.role && formik.errors.role && (
                        <span className="error" style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>{formik.errors.role}</span>
                      )}
                    </div>
                  </div>
                  {isEdit && (
                    <div className="col-md-2" style={{ marginTop: '0px' }}>
                      <div className="form-group">
                        <label style={{ fontWeight: '600', marginBottom: '8px' }}>Account Status</label>
                        <div style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                          <div 
                            onClick={() => {
                              if (isSelf) {
                                toast.warning("You cannot disable your own account status.");
                                return;
                              }
                              formik.setFieldValue("isActive", !formik.values.isActive);
                            }}
                            style={{ 
                              width: '50px', 
                              height: '24px', 
                              backgroundColor: formik.values.isActive ? '#10b981' : '#d1d5db', 
                              borderRadius: '12px', 
                              position: 'relative', 
                              cursor: isSelf ? 'not-allowed' : 'pointer',
                              transition: 'background-color 0.2s',
                              opacity: isSelf ? 0.6 : 1
                            }}
                          >
                            <div style={{ 
                              width: '20px', 
                              height: '20px', 
                              backgroundColor: '#fff', 
                              borderRadius: '50%', 
                              position: 'absolute', 
                              top: '2px', 
                              left: formik.values.isActive ? '28px' : '2px',
                              transition: 'left 0.2s'
                            }} />
                          </div>
                          <span style={{ marginLeft: '10px', fontSize: '14px', fontWeight: '500', color: formik.values.isActive ? '#059669' : '#6b7280' }}>
                            {formik.values.isActive ? 'Active' : 'Blocked'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                    <label style={{ margin: 0, fontWeight: '700', fontSize: '1.1rem', color: '#333' }}>Module Permissions</label>
                    <button 
                      type="button" 
                      className="btn btn-sm"
                      style={{ backgroundColor: '#004d2c', color: '#fff', borderRadius: '6px' }}
                      onClick={() => {
                        const allSelected = (formik.values.permission || []).length === AVAILABLE_PERMISSIONS.length;
                        if (allSelected) {
                          formik.setFieldValue("permission", []);
                        } else {
                          formik.setFieldValue("permission", AVAILABLE_PERMISSIONS.map(p => ({
                            title: p.title,
                            url: p.url,
                            icon: p.iconClass
                          })));
                        }
                      }}
                    >
                      {(formik.values.permission || []).length === AVAILABLE_PERMISSIONS.length ? 'Deselect All' : 'Select All Modules'}
                    </button>
                  </div>
                  <div className="admin-permission" style={{ backgroundColor: '#fcfcfc', padding: '20px', borderRadius: '10px', border: '1px solid #f0f0f0' }}>
                    {formik.touched.permission && formik.errors.permission && (
                      <span className="error" style={{ display: 'block', marginBottom: '15px', color: '#dc3545', fontWeight: '600' }}>
                        {formik.errors.permission}
                      </span>
                    )}
                    <div className="permission-groups" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                      {Object.entries(
                        AVAILABLE_PERMISSIONS.reduce((acc, permission) => {
                          const category = permission.category || "Other";
                          if (!acc[category]) acc[category] = [];
                          acc[category].push(permission);
                          return acc;
                        }, {})
                      ).map(([category, permissions]) => {
                        const categoryPermissions = permissions.map(p => p.title);
                        const selectedInCategory = (formik.values?.permission || []).filter(p => categoryPermissions.includes(p.title));
                        const isAllInCategorySelected = selectedInCategory.length === permissions.length;

                        return (
                          <div key={category} className="permission-group-section" style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '8px', border: '1px solid #eee', transition: 'all 0.3s ease' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', borderBottom: '1px solid #004d2c', paddingBottom: '6px' }}>
                              <h5 className="permission-category-title" style={{ fontWeight: 'bold', margin: 0, color: '#004d2c', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{category}</h5>
                              <button 
                                type="button"
                                style={{ fontSize: '9px', padding: '1px 5px', border: '1px solid #004d2c', borderRadius: '4px', background: isAllInCategorySelected ? '#004d2c' : '#fff', color: isAllInCategorySelected ? '#fff' : '#004d2c', cursor: 'pointer' }}
                                onClick={() => {
                                  if (isAllInCategorySelected) {
                                    formik.setFieldValue("permission", (formik.values.permission || []).filter(p => !categoryPermissions.includes(p.title)));
                                  } else {
                                    const newPermissions = [...(formik.values.permission || [])];
                                    permissions.forEach(p => {
                                      if (!newPermissions.some(np => np.title === p.title)) {
                                        newPermissions.push({ title: p.title, url: p.url, icon: p.iconClass });
                                      }
                                    });
                                    formik.setFieldValue("permission", newPermissions);
                                  }
                                }}
                              >
                                {isAllInCategorySelected ? 'None' : 'All'}
                              </button>
                            </div>
                            <ul style={{ listStyle: 'none', padding: 0 }}>
                              {permissions.map((permission) => (
                                <li key={permission.id} style={{ marginBottom: '4px' }}>
                                  <div className="permission-checkbx" style={{ display: 'flex', alignItems: 'center' }}>
                                    <input
                                      type="checkbox"
                                      id={permission.id}
                                      name="permission"
                                      value={permission.id}
                                      style={{ cursor: 'pointer', accentColor: '#004d2c' }}
                                      onChange={(e) => {
                                        const { checked } = e.target;
                                        if (checked) {
                                          formik.setFieldValue("permission", [
                                            ...(formik.values.permission || []),
                                            {
                                              title: permission.title,
                                              url: permission.url,
                                              icon: permission.iconClass,
                                            },
                                          ]);
                                        } else {
                                          formik.setFieldValue(
                                            "permission",
                                            (formik.values.permission || []).filter(
                                              (item) =>
                                                item.title !== permission.title
                                            )
                                          );
                                        }
                                      }}
                                      checked={(formik.values?.permission || []).some(
                                        (item) => item.title === permission.title
                                      )}
                                    />
                                    <label htmlFor={permission.id} style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '12px', color: '#444', marginBottom: 0 }}>
                                      {permission.title}
                                    </label>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <ConfirmationModal
        show={showResetModal}
        handleClose={() => setShowResetModal(false)}
        handleConfirm={handleResetPassword}
        title="Reset Administrator Password?"
        message="A new secure random password will be generated and sent to the administrator's email. The current password will stop working immediately."
        confirmText="Reset & Send Email"
        variant="danger"
        icon="fas fa-key"
      />
    </>
  );
};

export default SubAdminForm;
